import React, { useEffect } from "react";
import { ApolloProvider } from "@apollo/react-hooks";
import { componentSizes, Loader } from "@streeteasy/design-system";
import ButtonAction from "@streeteasy/design-system/button-action";
import moment from "moment-timezone";

import apolloClient from "src/shared/utilities/graphql/apolloClient";

import { useOHBehavior, useOHData } from "../../redux/hooks/openHousesHooks";
import { useUIBehavior } from "../../redux/hooks/uiHooks";
import CallModalCard from "../CallModalCard";
import MyActivityModal from "../MyActivityModal";
import OpenHousesList from "../OpenHousesList";
import OpenHousesListWrapper from "../OpenHousesListWrapper";
import * as S from "./styled";

const MyActivityOpenHouses = () => {
  const { clearOHDate, getOpenHouses } = useOHBehavior();
  const { closeModal, openModal } = useUIBehavior();

  const SHOW_BY_NUMBER = 5;
  const {
    OHDate,
    initialNumberUOH,
    isOHDrawerOpen,
    myRecentOpenHouses,
    myUpcomingOpenHouses,
    openHousesLoading,
    recentNumberOpenHouses,
    removedOHs,
    upcomingNumberOpenHouses
  } = useOHData();

  // positive numbers means OHDate is the past. 1 means it was yesterday
  const daysSinceOHDate = moment()
    .tz("America/New_York")
    .diff(moment(OHDate).tz("America/New_York"), "days");

  const showOHDateOnly = !daysSinceOHDate.isNaN && daysSinceOHDate > 0;

  const nextUpcomingNumberOpenHouses =
    upcomingNumberOpenHouses - SHOW_BY_NUMBER;
  const nextRecentNumberOpenHouses = recentNumberOpenHouses - SHOW_BY_NUMBER;

  useEffect(() => {
    getOpenHouses();

    if (isOHDrawerOpen) {
      openModal();
    }
  }, []);

  const renderOnlyOHDate = () => {
    const ctaButton = (
      <ButtonAction
        onClick={() => {
          if (myUpcomingOpenHouses.length === 0) {
            closeModal();
          }
          clearOHDate();
        }}
      >
        {myUpcomingOpenHouses.length === 0 ? "ok" : "see schedule"}
      </ButtonAction>
    );
    let openHousesOnOHDate = [];
    const openHousesForDay = myRecentOpenHouses.filter(ohDay =>
      moment(ohDay.date).isSame(moment(OHDate))
    )[0];
    if (openHousesForDay && openHousesForDay.my_open_house_items.length > 0) {
      openHousesOnOHDate = openHousesForDay.my_open_house_items.filter(
        oh => oh.attended === null
      );
      if (openHousesOnOHDate.length === 0) {
        // there were open houses, but all of them had attendance reported
        return (
          <S.SeeScheduleSection>
            <S.FeedbackRecordedIcon />
            <S.FeedbackRecordedTitle>
              Feedback recorded already
            </S.FeedbackRecordedTitle>
            <S.FeedbackCaption>
              Thank you! We appreciate your feedback.
            </S.FeedbackCaption>
            {myUpcomingOpenHouses.length > 0 && (
              <S.FeedbackRecordedCTACaption>
                See your full open house schedule.
              </S.FeedbackRecordedCTACaption>
            )}
            {ctaButton}
          </S.SeeScheduleSection>
        );
      }
      // there are open houses to report attendance for
      return (
        <OpenHousesListWrapper
          isUpcoming={false}
          nextNumberOpenHouses={openHousesOnOHDate.length - SHOW_BY_NUMBER}
          openHousesNumber={openHousesOnOHDate.length}
        >
          <OpenHousesList
            isPastOH
            listOfItems={[
              {
                date: OHDate,
                my_open_house_items: openHousesOnOHDate
              }
            ]}
          />
          {myUpcomingOpenHouses.length > 0 && (
            <S.SeeScheduleSection>
              <S.SeeScheduleCaption>
                See your full open house schedule.
              </S.SeeScheduleCaption>
              {ctaButton}
            </S.SeeScheduleSection>
          )}
        </OpenHousesListWrapper>
      );
    }
    // there were not any open houses on oh_date, probably because the date is too far in the past
    return (
      <S.SeeScheduleSection>
        <S.FeedbackPassedIcon />
        <S.FeedbackPassedTitle>
          The window to share feedback passed
        </S.FeedbackPassedTitle>
        {myUpcomingOpenHouses.length > 0 && (
          <S.FeedbackCaption>
            See what else is on your open house schedule.
          </S.FeedbackCaption>
        )}
        {ctaButton}
      </S.SeeScheduleSection>
    );
  };

  const renderNormalState = () => (
    <>
      {myUpcomingOpenHouses.length > 0 &&
        removedOHs.length !== initialNumberUOH && (
          <OpenHousesListWrapper
            nextNumberOpenHouses={nextUpcomingNumberOpenHouses}
            openHousesNumber={upcomingNumberOpenHouses}
          >
            <OpenHousesList listOfItems={myUpcomingOpenHouses} />
          </OpenHousesListWrapper>
        )}
      {removedOHs.length === initialNumberUOH && (
        <OpenHousesListWrapper nextNumberOpenHouses={0} openHousesNumber={0} />
      )}
      {myRecentOpenHouses.length > 0 && (
        <OpenHousesListWrapper
          isUpcoming={false}
          nextNumberOpenHouses={nextRecentNumberOpenHouses}
          openHousesNumber={recentNumberOpenHouses}
        >
          <OpenHousesList isPastOH listOfItems={myRecentOpenHouses} />
        </OpenHousesListWrapper>
      )}
    </>
  );

  return (
    <ApolloProvider client={apolloClient}>
      <S.Wrapper>
        <CallModalCard />
        <MyActivityModal className="a11y" rootElementId="#content">
          {openHousesLoading && (
            <Loader
              labelText="Loading open house information..."
              size={componentSizes.LG}
            />
          )}
          {!openHousesLoading &&
            (showOHDateOnly ? renderOnlyOHDate() : renderNormalState())}
        </MyActivityModal>
      </S.Wrapper>
    </ApolloProvider>
  );
};

export default MyActivityOpenHouses;
